import {
    uploadPreview,
    uploadCommit,
    type SuggestedMapping,
    type UploadMappingItem,
} from '@api/vendor-portal';
import PageHeader from '@components/common/PageHeader';
import {
    Alert,
    Badge,
    Box,
    Button,
    FileInput,
    Group,
    Radio,
    Select,
    Stack,
    Stepper,
    Text,
} from '@mantine/core';
import { CheckCircle, Upload, WarningCircle } from '@phosphor-icons/react';
import { useMutation } from '@tanstack/react-query';
import { type FC, useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import {
    DataSheet,
    type DataSheetColumn,
} from '@components/ui/DataSheet';

// ─── Types ─────────────────────────────────────────────────────

interface MappingRow extends SuggestedMapping {
    selectedMaterialId: string | null;
    selectedMaterialName: string | null;
}

// ─── Main Container ────────────────────────────────────────────

const VendorUploadContainer: FC = () => {
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState(0);

    // Step 1 state
    const [file, setFile] = useState<File | null>(null);
    const [format, setFormat] = useState<string>('A');
    const [csvContent, setCsvContent] = useState<string>('');

    // Step 2 state
    const [mappings, setMappings] = useState<MappingRow[]>([]);
    const [previewRows, setPreviewRows] = useState<Record<string, string>[]>([]);
    const [unmatchedNames, setUnmatchedNames] = useState<string[]>([]);

    // Step 3 state
    const [processedCount, setProcessedCount] = useState(0);
    const [uploadErrors, setUploadErrors] = useState<string[]>([]);

    // Mutations
    const previewMutation = useMutation(uploadPreview, {
        onSuccess: (data) => {
            setPreviewRows(data.rows);
            setUnmatchedNames(data.unmatchedNames);
            setMappings(
                data.suggestedMappings.map((m) => ({
                    ...m,
                    selectedMaterialId: m.suggestedMaterialId,
                    selectedMaterialName: m.suggestedMaterialName,
                })),
            );
            setActiveStep(1);
        },
    });

    const commitMutation = useMutation(uploadCommit, {
        onSuccess: (data) => {
            setProcessedCount(data.processed);
            setUploadErrors(data.errors);
            setActiveStep(2);
        },
    });

    // Handlers
    const handleFileChange = useCallback((newFile: File | null) => {
        setFile(newFile);
        if (newFile) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setCsvContent(e.target?.result as string ?? '');
            };
            reader.readAsText(newFile);
        } else {
            setCsvContent('');
        }
    }, []);

    const handlePreview = useCallback(() => {
        if (!csvContent) return;
        previewMutation.mutate({ format, data: csvContent });
    }, [csvContent, format, previewMutation]);

    const handleCommit = useCallback(() => {
        const confirmedMappings: UploadMappingItem[] = mappings
            .filter((m) => m.selectedMaterialId)
            .map((m) => ({
                uploadName: m.uploadName,
                materialId: m.selectedMaterialId!,
            }));

        commitMutation.mutate({
            format,
            data: csvContent,
            mappings: confirmedMappings,
        });
    }, [mappings, format, csvContent, commitMutation]);

    const updateMapping = useCallback((uploadName: string, materialId: string | null) => {
        setMappings((prev) =>
            prev.map((m) =>
                m.uploadName === uploadName
                    ? { ...m, selectedMaterialId: materialId, selectedMaterialName: materialId ? materialId : null }
                    : m,
            ),
        );
    }, []);

    // Mapping table columns
    const mappingColumns: DataSheetColumn<MappingRow>[] = [
        {
            key: 'uploadName',
            header: 'Name in CSV',
            width: 200,
            sticky: true,
            render: (val: string, row: MappingRow) => {
                const isUnmatched = unmatchedNames.includes(val);
                return (
                    <Group gap={4} wrap="nowrap">
                        <Text size="sm" fw={isUnmatched ? 600 : 400} c={isUnmatched ? 'red' : undefined}>
                            {val}
                        </Text>
                        {isUnmatched && (
                            <Badge color="red" variant="light" size="xs">
                                Unmatched
                            </Badge>
                        )}
                    </Group>
                );
            },
        },
        {
            key: 'suggestedMaterialName',
            header: 'Suggested Material',
            width: 200,
            render: (val: string | null) => val ?? '--',
        },
        {
            key: 'confidence',
            header: 'Confidence',
            width: 100,
            align: 'center' as const,
            render: (val: number) => {
                const pct = Math.round(val * 100);
                const color = pct >= 80 ? 'green' : pct >= 50 ? 'yellow' : 'red';
                return (
                    <Badge color={color} variant="light" size="sm">
                        {pct}%
                    </Badge>
                );
            },
        },
        {
            key: '_action',
            header: 'Override',
            width: 200,
            render: (_val: unknown, row: MappingRow) => (
                <Select
                    size="xs"
                    placeholder="Keep suggestion"
                    value={row.selectedMaterialId}
                    onChange={(val) => updateMapping(row.uploadName, val)}
                    data={mappings.map((m) => ({
                        value: m.suggestedMaterialId ?? m.uploadName,
                        label: m.suggestedMaterialName ?? m.uploadName,
                    }))}
                    clearable
                    searchable
                />
            ),
        },
    ];

    // Count how many mappings are resolved
    const resolvedCount = mappings.filter((m) => m.selectedMaterialId).length;
    const totalCount = mappings.length;

    return (
        <div className="flex h-full w-full flex-col page-shell">
            <PageHeader
                icon="📤"
                title="Upload Data"
                subtitle="Import stock events from a CSV file"
            />

            <Box mt="lg">
                <Stepper active={activeStep} size="sm">
                    <Stepper.Step label="Select File" description="Choose CSV and format">
                        <Stack gap="md" mt="lg" maw={500}>
                            <Radio.Group
                                label="Upload Format"
                                value={format}
                                onChange={setFormat}
                            >
                                <Group mt="xs" gap="lg">
                                    <Radio value="A" label="Format A — Full History" />
                                    <Radio value="B" label="Format B — Opening Balance" />
                                </Group>
                            </Radio.Group>

                            {format === 'A' && (
                                <Alert variant="light" color="blue">
                                    <Text size="xs">
                                        Expected columns: Date, Material Name, Event Type, Quantity, Reference, Notes
                                    </Text>
                                </Alert>
                            )}
                            {format === 'B' && (
                                <Alert variant="light" color="blue">
                                    <Text size="xs">
                                        Expected columns: Material Name, Balance, As-of Date
                                    </Text>
                                </Alert>
                            )}

                            <FileInput
                                label="CSV File"
                                placeholder="Click to select a .csv file"
                                accept=".csv"
                                value={file}
                                onChange={handleFileChange}
                                leftSection={<Upload size={16} />}
                            />

                            {csvContent && (
                                <Text size="xs" c="dimmed">
                                    File loaded: {file?.name} ({previewRows.length > 0 ? `${previewRows.length} rows` : 'ready to preview'})
                                </Text>
                            )}

                            <Group justify="flex-end">
                                <Button
                                    onClick={handlePreview}
                                    loading={previewMutation.isLoading}
                                    disabled={!csvContent}
                                >
                                    Preview
                                </Button>
                            </Group>
                        </Stack>
                    </Stepper.Step>

                    <Stepper.Step label="Map Materials" description="Confirm name mappings">
                        <Stack gap="md" mt="lg">
                            <Group justify="space-between">
                                <Text size="sm" c="dimmed">
                                    {resolvedCount} of {totalCount} materials mapped
                                </Text>
                                {unmatchedNames.length > 0 && (
                                    <Badge color="red" variant="light" leftSection={<WarningCircle size={12} />}>
                                        {unmatchedNames.length} unmatched
                                    </Badge>
                                )}
                            </Group>

                            <DataSheet
                                data={mappings}
                                columns={mappingColumns}
                                getRowKey={(row) => row.uploadName}
                                hideHeaderBar
                                bare
                                maxHeight={400}
                            />

                            <Group justify="flex-end">
                                <Button variant="default" onClick={() => setActiveStep(0)}>
                                    Back
                                </Button>
                                <Button
                                    onClick={handleCommit}
                                    loading={commitMutation.isLoading}
                                    disabled={resolvedCount === 0}
                                >
                                    Confirm & Upload
                                </Button>
                            </Group>
                        </Stack>
                    </Stepper.Step>

                    <Stepper.Step label="Results" description="Upload complete">
                        <Stack gap="md" mt="lg" maw={500}>
                            <Alert
                                color={uploadErrors.length === 0 ? 'green' : 'yellow'}
                                variant="light"
                                icon={<CheckCircle size={20} />}
                            >
                                <Text size="sm" fw={600}>
                                    {processedCount} events processed successfully
                                </Text>
                            </Alert>

                            {uploadErrors.length > 0 && (
                                <Alert color="red" variant="light" icon={<WarningCircle size={20} />}>
                                    <Stack gap={4}>
                                        <Text size="sm" fw={600}>{uploadErrors.length} errors:</Text>
                                        {uploadErrors.map((err, i) => (
                                            <Text key={i} size="xs">{err}</Text>
                                        ))}
                                    </Stack>
                                </Alert>
                            )}

                            <Group justify="flex-end">
                                <Button onClick={() => navigate('/vendor/stock')}>
                                    Done
                                </Button>
                            </Group>
                        </Stack>
                    </Stepper.Step>
                </Stepper>
            </Box>
        </div>
    );
};

export default VendorUploadContainer;
